import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import Header from "../../pages/header/Header";
import Menu from "../../pages/sidebar/Menu";
import { useAuth } from "../../contexts/AuthContext";
import WelcomeDialog from "../../components/welcome-dialog/WelcomeDialog";
import { updateUserWelcomeVersion } from "../../database/firestore";
import OfflineStatusBanner from "../../components/connectivity/OfflineStatusBanner";

const WELCOME_VERSION = "2.0";

const SIDEBAR_WIDTH = 300;

export const AppLayout: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { currentUser, userData } = useAuth();
  const [welcomeDialogOpen, setWelcomeDialogOpen] = useState(false);

  // Check if user needs to see the welcome dialog
  useEffect(() => {
    if (
      currentUser &&
      userData &&
      !userData.WelcomeDialogSeenVersions?.includes(WELCOME_VERSION)
    ) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        setWelcomeDialogOpen(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentUser, userData]);

  const handleWelcomeDialogClose = async () => {
    if (currentUser) {
      try {
        await updateUserWelcomeVersion(currentUser.uid, WELCOME_VERSION);
        setWelcomeDialogOpen(false);
      } catch (error) {
        console.error("[AppLayout][handleWelcomeDialogClose] - Error updating welcome version:", error);
        // Close dialog even if update fails
        setWelcomeDialogOpen(false);
      }
    } else {
      setWelcomeDialogOpen(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "var(--background-default)",
        color: "var(--text-primary)",
      }}
    >
      {isDesktop && (
        <Box
          component="aside"
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            borderRight: "1px solid var(--divider)",
            backgroundColor: "var(--background-paper)",
            position: "sticky",
            top: 0,
            maxHeight: "100vh",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Menu />
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <Header showDrawer={!isDesktop} />
        <OfflineStatusBanner />
        <Box
          component="main"
          sx={{
            flex: 1,
            width: "100%",
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 3 },
            backgroundColor: "var(--background-default)",
            overflowX: "hidden",
          }}
        >
          <Outlet />
        </Box>
      </Box>
      <WelcomeDialog
        open={welcomeDialogOpen}
        onClose={handleWelcomeDialogClose}
        onGetStarted={handleWelcomeDialogClose}
      />
    </Box>
  );
};

export default AppLayout;
